"use client";
import { useCallback, useRef } from "react";

const SAMPLE_RATE = 24000;

export interface AudioStreamStats {
  gaps: number;
  totalGapMs: number;
  chunks: number;
}

export function useAudioStreamQueue() {
  const ctxRef = useRef<AudioContext | null>(null);
  const nextTimeRef = useRef(0);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const statsRef = useRef<AudioStreamStats>({ gaps: 0, totalGapMs: 0, chunks: 0 });

  const enqueue = useCallback((wavBuf: ArrayBuffer) => {
    if (!ctxRef.current) ctxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
    const ctx = ctxRef.current;
    // Chain decodes to guarantee playback order
    chainRef.current = chainRef.current.then(async () => {
      const audioBuffer = await ctx.decodeAudioData(wavBuf.slice(0));
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      const now = ctx.currentTime;
      const startAt = Math.max(now, nextTimeRef.current);
      // Gap detection: if playback head passed nextTime, we starved
      if (statsRef.current.chunks > 0 && now > nextTimeRef.current) {
        const gapMs = Math.round((now - nextTimeRef.current) * 1000);
        statsRef.current.gaps++;
        statsRef.current.totalGapMs += gapMs;
      }
      statsRef.current.chunks++;
      source.start(startAt);
      nextTimeRef.current = startAt + audioBuffer.duration;
    });
  }, []);

  const getStats = useCallback((): AudioStreamStats => ({ ...statsRef.current }), []);

  const waitForDrain = useCallback((): Promise<void> => {
    return chainRef.current.then(() => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      const remaining = nextTimeRef.current - ctx.currentTime;
      if (remaining <= 0) return;
      return new Promise(resolve => setTimeout(resolve, remaining * 1000));
    });
  }, []);

  const resetQueue = useCallback(() => {
    nextTimeRef.current = 0;
    chainRef.current = Promise.resolve();
    statsRef.current = { gaps: 0, totalGapMs: 0, chunks: 0 };
    if (ctxRef.current) {
      ctxRef.current.close();
      ctxRef.current = null;
    }
  }, []);

  return { enqueue, resetQueue, getStats, waitForDrain };
}
